import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { PartnerType, BusinessSector, PartnerTypeFormData, BusinessSectorFormData } from '../types';

interface PartnerConfigStore {
  // Partner Types State
  partnerTypes: PartnerType[];
  partnerTypesLoading: boolean;
  partnerTypesError: string | null;
  
  // Business Sectors State
  businessSectors: BusinessSector[];
  businessSectorsLoading: boolean;
  businessSectorsError: string | null;
  
  // Partner Types Actions
  fetchPartnerTypes: () => Promise<void>;
  createPartnerType: (data: PartnerTypeFormData) => Promise<PartnerType | null>;
  updatePartnerType: (id: string, data: Partial<PartnerTypeFormData>) => Promise<PartnerType | null>;
  deletePartnerType: (id: string) => Promise<boolean>;
  reorderPartnerTypes: (partnerTypes: PartnerType[]) => Promise<boolean>;
  
  // Business Sectors Actions
  fetchBusinessSectors: () => Promise<void>;
  createBusinessSector: (data: BusinessSectorFormData) => Promise<BusinessSector | null>;
  updateBusinessSector: (id: string, data: Partial<BusinessSectorFormData>) => Promise<BusinessSector | null>;
  deleteBusinessSector: (id: string) => Promise<boolean>;
  reorderBusinessSectors: (businessSectors: BusinessSector[]) => Promise<boolean>;
  
  // Utility Actions
  clearErrors: () => void;
}

export const usePartnerConfigStore = create<PartnerConfigStore>((set, get) => ({
  // Initial state
  partnerTypes: [],
  partnerTypesLoading: false,
  partnerTypesError: null,
  businessSectors: [],
  businessSectorsLoading: false,
  businessSectorsError: null,

  // Partner Types Actions
  fetchPartnerTypes: async () => {
    try {
      set({ partnerTypesLoading: true, partnerTypesError: null });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('partner_types')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      set({ partnerTypes: data || [] });
    } catch (error) {
      console.error('Error fetching partner types:', error);
      set({ partnerTypesError: friendlyDbError(error) });
    } finally {
      set({ partnerTypesLoading: false });
    }
  },

  createPartnerType: async (data: PartnerTypeFormData) => {
    try {
      set({ partnerTypesError: null });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data: newPartnerType, error } = await supabase
        .from('partner_types')
        .insert({
          ...data,
          organization_id: profileData.organization_id,
          created_by: userData.user.id,
          updated_by: userData.user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        partnerTypes: [...state.partnerTypes, newPartnerType]
      }));

      return newPartnerType;
    } catch (error) {
      console.error('Error creating partner type:', error);
      set({ partnerTypesError: friendlyDbError(error) });
      return null;
    }
  },

  updatePartnerType: async (id: string, data: Partial<PartnerTypeFormData>) => {
    try {
      set({ partnerTypesError: null });

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: updatedPartnerType, error } = await supabase
        .from('partner_types')
        .update({
          ...data,
          updated_by: userData.user.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        partnerTypes: state.partnerTypes.map(pt =>
          pt.id === id ? updatedPartnerType : pt
        )
      }));

      return updatedPartnerType;
    } catch (error) {
      console.error('Error updating partner type:', error);
      set({ partnerTypesError: friendlyDbError(error) });
      return null;
    }
  },

  deletePartnerType: async (id: string) => {
    try {
      set({ partnerTypesError: null });

      // Check if partner type is in use
      const { count } = await supabase
        .from('external_partners')
        .select('id', { count: 'exact' })
        .eq('partner_type', id);

      if (count && count > 0) {
        throw new Error('Impossibile eliminare: tipo partner in uso');
      }

      const { error } = await supabase
        .from('partner_types')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        partnerTypes: state.partnerTypes.filter(pt => pt.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting partner type:', error);
      set({ partnerTypesError: friendlyDbError(error) });
      return false;
    }
  },

  reorderPartnerTypes: async (partnerTypes: PartnerType[]) => {
    try {
      set({ partnerTypesError: null });

      const updates = partnerTypes.map((pt, index) => ({
        id: pt.id,
        sort_order: index
      }));

      const { error } = await supabase.rpc('update_partner_types_order', {
        updates: updates
      });

      if (error) throw error;

      // Update local state
      set({ partnerTypes });

      return true;
    } catch (error) {
      console.error('Error reordering partner types:', error);
      set({ partnerTypesError: friendlyDbError(error) });
      return false;
    }
  },

  // Business Sectors Actions
  fetchBusinessSectors: async () => {
    try {
      set({ businessSectorsLoading: true, businessSectorsError: null });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('business_sectors')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      set({ businessSectors: data || [] });
    } catch (error) {
      console.error('Error fetching business sectors:', error);
      set({ businessSectorsError: friendlyDbError(error) });
    } finally {
      set({ businessSectorsLoading: false });
    }
  },

  createBusinessSector: async (data: BusinessSectorFormData) => {
    try {
      set({ businessSectorsError: null });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data: newBusinessSector, error } = await supabase
        .from('business_sectors')
        .insert({
          ...data,
          organization_id: profileData.organization_id,
          created_by: userData.user.id,
          updated_by: userData.user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        businessSectors: [...state.businessSectors, newBusinessSector]
      }));

      return newBusinessSector;
    } catch (error) {
      console.error('Error creating business sector:', error);
      set({ businessSectorsError: friendlyDbError(error) });
      return null;
    }
  },

  updateBusinessSector: async (id: string, data: Partial<BusinessSectorFormData>) => {
    try {
      set({ businessSectorsError: null });

      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: updatedBusinessSector, error } = await supabase
        .from('business_sectors')
        .update({
          ...data,
          updated_by: userData.user.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        businessSectors: state.businessSectors.map(bs =>
          bs.id === id ? updatedBusinessSector : bs
        )
      }));

      return updatedBusinessSector;
    } catch (error) {
      console.error('Error updating business sector:', error);
      set({ businessSectorsError: friendlyDbError(error) });
      return null;
    }
  },

  deleteBusinessSector: async (id: string) => {
    try {
      set({ businessSectorsError: null });

      // Check if business sector is in use
      const { count } = await supabase
        .from('external_partners')
        .select('id', { count: 'exact' })
        .eq('business_sector', id);

      if (count && count > 0) {
        throw new Error('Impossibile eliminare: settore business in uso');
      }

      const { error } = await supabase
        .from('business_sectors')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        businessSectors: state.businessSectors.filter(bs => bs.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting business sector:', error);
      set({ businessSectorsError: friendlyDbError(error) });
      return false;
    }
  },

  reorderBusinessSectors: async (businessSectors: BusinessSector[]) => {
    try {
      set({ businessSectorsError: null });

      const updates = businessSectors.map((bs, index) => ({
        id: bs.id,
        sort_order: index
      }));

      const { error } = await supabase.rpc('update_business_sectors_order', {
        updates: updates
      });

      if (error) throw error;

      // Update local state
      set({ businessSectors });

      return true;
    } catch (error) {
      console.error('Error reordering business sectors:', error);
      set({ businessSectorsError: friendlyDbError(error) });
      return false;
    }
  },

  // Utility Actions
  clearErrors: () => {
    set({
      partnerTypesError: null,
      businessSectorsError: null
    });
  }
}));